import {FC} from 'react';
import {GridColDef, GridValueGetterParams} from '@mui/x-data-grid';
import {EmployeeDTO} from 'src/types/employee';
import {Stack, Tooltip, Typography} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import {PaymentModeStatus} from 'src/components/EmployeesTable/PaymentModeStatus';
import {ConsumptionPaymentMode, RegistrationStatusEnum, UserTypeEnum} from 'src/enums';
import {EmployeeStatus} from 'src/components/EmployeesTable/EmployeeStatus/EmployeeStatus';
import {EmployeeActions} from 'src/components/EmployeesTable/EmployeeActions';
import {useMissingBexio} from 'src/hooks/useMissingBexio';
import dayjs from 'dayjs';
import {Link} from 'react-router-dom';

// The employee page is reached with the tenant it is being read in, so the link is built in one
// place and used by both the grid cell and the card.
export const buildEmployeeLink = (employeeId: number, businessId?: number, companyId?: number): string =>
    `/app/users/employee/${employeeId}?businessId=${businessId || 0}&companyId=${companyId || 0}`;

export const EmployeeNameCell: FC<{row: EmployeeDTO; businessId?: number; companyId?: number}> = ({
                                                                                                     row,
                                                                                                     businessId,
                                                                                                     companyId,
                                                                                                 }) => {
    const firstName = row.firstName || '';
    const lastName = row.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    // Red "no Bexio" flag only for self-paying employees; company-paid stay blue. Through the hook,
    // so this row and the card that replaces it below a desktop cannot disagree - and so the viewer's
    // side of the rule is applied here too: this grid used to pass no viewer at all, which showed the
    // warning to the one audience the rule says must never see it, a company looking at its own staff.
    const showMissingBexio = useMissingBexio({
        hasBexio: !!row.user?.bexioDataList?.length,
        userType: UserTypeEnum.EMPLOYEE,
        paymentMode: row.consumptionPaymentMode,
    });

    return (
        <Stack>
            <Tooltip
                title={showMissingBexio ? <FormattedMessage id="noBexioIntegration"/> : ''}
                arrow
            >
                <Link to={buildEmployeeLink(row.id, businessId, companyId)}>
                    <Typography
                        variant="body2"
                        sx={{fontWeight: 500, color: showMissingBexio ? 'error.main' : 'primary.main'}}
                    >
                        {fullName || 'N/A'}
                    </Typography>
                </Link>
            </Tooltip>
        </Stack>
    );
};

export const columnConfigCompanyEmployees = (businessId?: number, companyId?: number): GridColDef<EmployeeDTO>[] => [
    {
        field: 'fullName',
        headerName: 'Name',
        flex: 2,
        minWidth: 160,
        sortable: true,
        renderCell: (params) => (
            <EmployeeNameCell
                row={params.row}
                businessId={businessId}
                companyId={companyId}
            />
        ),
    },
    {
        field: 'email',
        headerName: 'Email',
        flex: 2,
        minWidth: 160,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => params.row.user?.emailAddress || 'N/A',
        renderCell: (params) => (
            <Typography variant="body2">
                {params.row.user?.emailAddress || 'N/A'}
            </Typography>
        ),
    },
    {
        field: 'createdAt',
        headerName: 'Created',
        flex: 1,
        minWidth: 100,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => dayjs(params.row.user?.created).format('DD/MM/YY'),
        renderCell: (params) => (
            <Typography variant="body2">
                {dayjs(params.row.user?.created).format('DD/MM/YY')}
            </Typography>
        ),
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        sortable: true,
        renderCell: (params) => (
            <EmployeeStatus
                label={params.row.user?.registrationStatus as RegistrationStatusEnum}
            />
        ),
    },
    {
        field: 'consumptionPaymentMode',
        headerName: 'Payment Mode',
        flex: 1.4,
        minWidth: 140,
        sortable: true,
        renderCell: (params) => (
            <PaymentModeStatus
                label={params.row.consumptionPaymentMode as ConsumptionPaymentMode}
            />
        ),
    },
    {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        cellClassName: 'ampTableCell',
        align: 'right' as const,
        headerAlign: 'right' as const,
        renderCell: (params) => (
            <EmployeeActions row={params.row}/>
        ),
    },
];
